import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import axios from "axios";
import jwtDecode from "jwt-decode";
import { appUrl } from "../../../../../utils/config";
import { convertToLocaleDateTime } from "../functionalComponents/FormatDateTimeToLocale";



function getApiBase() {
  return `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;
}




/** Map API inventoryReconciliationData row to table row keys */
function mapReconciliationRowToTable(row, index) {
  const code = row.sponsorMaterialCode ?? "";
  const desc = row.sponsorMaterialDescription ?? "";
  return {
    id: row.collabrixReconciliationID ? `${row.collabrixReconciliationID}-${index}` : `row-${index}`,
    plantId: row.vendorPlant ?? "",
    bmsMaterialCodeDescription: desc ? `${code} / ${desc}`.trim() : code,
    bmsUom: row.sponsorUOM ?? "",
    bmsBatchNumber: row.sponsorBatchNumber ?? "",
    cmoBatchNumber: row.cmoBatchNumber ?? "",
    bmsOnHandQty: row.sponsorOnHandQty ? Number(row.sponsorOnHandQty) % 1 !== 0 ? Number(row.sponsorOnHandQty).toFixed(3) : row.sponsorOnHandQty : '',
    cmoOnHandQty: row.cmoOnHandQty ? Number(row.cmoOnHandQty) % 1 !== 0 ? Number(row.cmoOnHandQty).toFixed(3) : row.cmoOnHandQty : '',
    difference: row.difference ? Number(row.difference) % 1 !== 0 ? Number(row.difference).toFixed(3) : row.difference : '',
    reasonForDifference: row.reasonForDifference ?? "",
  };
}

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxHeight: "100vh",
    height: "100vh",
    margin: 0,
    borderRadius: 0,
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: 0,
    paddingTop: 0,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    "&.MuiDialogContent-root:first-child": {
      paddingTop: 0,
    },
    "@media print": {
      height: "auto !important",
      overflow: "visible !important",
      "& .no-print": {
        display: "none !important",
      },
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexShrink: 0,
    padding: theme.spacing(0, 3, 2.5, 3),
    backgroundColor: "#fafafa",
    borderBottom: "2px solid rgba(0, 0, 0, 0.12)",
    minHeight: 64,
  },
  backButton: {
    textTransform: "none",
    color: theme.palette.primary.main,
    padding: theme.spacing(1, 1.5),
    minWidth: "auto",
    marginLeft: theme.spacing(-1),
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: "1.25rem",
  },
  layout: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    "@media print": {
      flex: "none !important",
      minHeight: 0,
      overflow: "visible !important",
      height: "auto !important",
      "& .no-print": {
        display: "none !important",
      },
    },
  },
  leftPanel: {
    flex: "0 0 320px",
    maxWidth: 320,
    overflow: "hidden",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
    boxShadow: "2px 0 8px rgba(0, 0, 0, 0.06)",
    borderRadius: 4,
    padding: theme.spacing(0, 2, 2, 2),
  },
  historySectionTitle: {
    padding: theme.spacing(2, 2, 1.5, 2),
    marginBottom: theme.spacing(1),
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    flexShrink: 0,
  },
  historyListWrapper: {
    position: "relative",
    flex: 1,
    padding: theme.spacing(2, 2, 2, 0),
    minHeight: 0,
    overflow: "auto",
  },
  historyListInner: {
    position: "relative",
    minHeight: "100%",
  },
  historyTimelineLine: {
    position: "absolute",
    left: "10px",
    width: "10px",
    backgroundColor: "#1976d2",
    zIndex: 0,
    pointerEvents: "none",
  },
  historyList: {
    position: "relative",
    zIndex: 1,
    listStyle: "none",
    margin: 0,
    padding: theme.spacing(0.5, 0),
  },
  historyItem: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(1.5, 1.5, 1.5, 0.5),
    marginLeft: theme.spacing(-0.5),
    cursor: "pointer",
    position: "relative",
    borderRadius: 6,
    outline: "none",
    "&:hover": {
      "& $historyLabelLink": {
        textDecoration: "underline",
      },
    },
    "&:focus-visible": {
      boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.5)",
    },
  },
  historyItemSelected: {
    backgroundColor: "rgba(25, 118, 210, 0.08)",
    "& $historyLabelText": {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
  },
  historyNumber: {
    width: 28,
    height: 28,
    minWidth: 28,
    borderRadius: "50%",
    backgroundColor: "#1976d2",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8125rem",
    fontWeight: 600,
    flexShrink: 0,
    // marginRight: 14,
    position: "relative",
    zIndex: 1,
    boxSizing: "border-box",
  },
  historyNumberInactive: {
    backgroundColor: "#fff",
    color: "#1976d2",
    border: "2px solid #1976d2",
  },
  historyLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: "0.8125rem",
    lineHeight: 1.5,
    wordBreak: "break-word",
  },
  historyLabelLink: {
    color: "#1976d2",
    textDecoration: "underline",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  historyLabelText: {
    color: "inherit",
  },
  historyLabelLine: {
    display: "block",
  },
  historyLabelLineFirst: {
    fontWeight: 600,
  },
  rightPanel: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: "#fafafa",
    padding: theme.spacing(2.5, 3),
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, 0.08)",
    "@media print": {
      overflow: "visible !important",
      height: "auto !important",
      minHeight: 0,
    },
  },
  filePreviewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexShrink: 0,
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },
  filePreviewTitle: {
    fontWeight: 600,
    fontSize: "1.125rem",
    paddingTop: theme.spacing(0.5),
  },
  downloadPdfButton: {
    backgroundColor: "#ec6535",
    color: "#fff",
    textTransform: "none",
    borderRadius: "2rem",
    padding: theme.spacing(1.25, 2.5),
    "&:hover": {
      backgroundColor: "#9E4D27",
      color: "#fff",
    },
  },
  pdfHint: {
    marginTop: theme.spacing(1),
    color: "rgba(0, 0, 0, 0.6)",
    fontSize: "0.75rem",
  },
  tableContainer: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    boxShadow: "none",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 4,
    backgroundColor: "#fff",
    padding: 0,
    "@media print": {
      overflow: "visible !important",
      maxHeight: "none !important",
      height: "auto !important",
      flex: "none !important",
    },
  },
  table: {
    minWidth: 650,
    borderCollapse: "collapse",
    "& .MuiTableCell-stickyHeader": {
      backgroundColor: "#FFE4C2",
      borderTop: "1px solid #ffab91",
      borderBottom: "1px solid #ffab91",
      padding: theme.spacing(1.5, 2),
      fontSize: "0.875rem",
      fontWeight: 500,
    },
    "& .MuiTableCell-root": {
      padding: theme.spacing(1.5, 2),
      fontSize: "0.875rem",
    },
  },
  grayRow: {
    backgroundColor: "#f7f6f6",
  },
  emptyState: {
    padding: theme.spacing(4, 3),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
    padding: theme.spacing(3),
  },
  errorBox: {
    padding: theme.spacing(2, 2.5),
    backgroundColor: "rgba(255, 0, 0, 0.08)",
    border: "1px solid rgba(255, 0, 0, 0.3)",
    borderRadius: 4,
    marginBottom: theme.spacing(2),
    color: theme.palette.error.dark,
    fontSize: "0.875rem",
  },
  printOnly: {
    display: "none",
    "@media print": {
      display: "block",
    },
  },
}));

const TABLE_COLUMNS = [
  { id: "plantId", label: "Plant ID" },
  { id: "bmsMaterialCodeDescription", label: "BMS Material Code / Description" },
  { id: "bmsUom", label: "BMS UoM" },
  { id: "bmsBatchNumber", label: "BMS Batch Number" },
  { id: "cmoBatchNumber", label: "CMO Batch Number" },
  { id: "bmsOnHandQty", label: "BMS on-hand qty." },
  { id: "cmoOnHandQty", label: "CMO on-hand qty." },
  { id: "difference", label: "Difference (BMS - CMO)" },
  { id: "reasonForDifference", label: "Reason for Difference" },
];

function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function getCellValue(row, colId) {
  const val = row[colId] ?? row[colId.replace(/([A-Z])/g, "_$1").toLowerCase()] ?? "";
  return String(val);
}

export default function ViewReconciliationHistory(props) {
  const { open = false, onClose, vendorMosymphonyID, cmoName = "" } = props;
  const classes = useStyles();
  const [historyList, setHistoryList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [tableError, setTableError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // Fetch reconciliation history on open (fetchInventoryReconciliationHistory)
  useEffect(() => {
    if (!open || !vendorMosymphonyID) {
      setHistoryList([]);
      setSelectedId(null);
      setListError(vendorMosymphonyID ? null : "Please select a CMO to view reconciliation history.");
      return;
    }
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    setHistoryList([]);
    setSelectedId(null);
    setTableData([]);

    let brandOwnerMosymphonyID = "";

    const token = localStorage.getItem("authToken");
    if (token) {
      const decoded = jwtDecode(token);
      brandOwnerMosymphonyID = decoded?.organization_id ?? "";
    }

    const processHistoryResponse = (data) => {
      if (!data || data.status === false) {
        setListError(data?.errorMessage || "Failed to load reconciliation history.");
        setHistoryList([]);
        return;
      }
      const rawList = data.inventoryReconciliationHistory ?? [];
      const arr = Array.isArray(rawList) ? rawList : [];
      const sorted = [...arr].sort((a, b) => {
        const dateA = a.reconciliationDateTimeStamp ? new Date(a.reconciliationDateTimeStamp).getTime() : 0;
        const dateB = b.reconciliationDateTimeStamp ? new Date(b.reconciliationDateTimeStamp).getTime() : 0;
        return dateB - dateA;
      });
      const displayList = sorted.map((item) => {
        const creationDateStr = convertToLocaleDateTime(item.reconciliationDateTimeStamp);
        return {
          collabrixReconciliationID: item.collabrixReconciliationID,
          reconciliationDateTimeStamp: creationDateStr,
        };
      });
      setHistoryList(displayList);
      if (displayList.length > 0) {
        setSelectedId(displayList[0].collabrixReconciliationID);
      }
    };

    const url = `${getApiBase()}/fetchInventoryReconciliationHistory`;
    axios
      .post(url, { brandOwnerMosymphonyID, vendorMosymphonyID })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.status) {
          processHistoryResponse(res.data);

        } else {
          setListError("Something went wrong. Please try again later.");
          setHistoryList([]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err?.response?.data?.errorMessage || err?.response?.data?.message || err?.message || "Failed to load reconciliation history.";
          setListError(msg);
          setHistoryList([]);
        }
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, vendorMosymphonyID, cmoName]);

  // Fetch table data when selected history changes (fetchInventoryReconciliationData)
  useEffect(() => {
    if (!open || selectedId == null || !vendorMosymphonyID) {
      setTableData([]);
      return;
    }
    let cancelled = false;
    setTableLoading(true);
    setTableError(null);

    let brandOwnerMosymphonyID = "";

    const token = localStorage.getItem("authToken");
    if (token) {
      const decoded = jwtDecode(token);
      brandOwnerMosymphonyID = decoded?.organization_id ?? "";
    }

    const processTableResponse = (data) => {
      if (!data || data.status === false) {
        setTableError(data?.errorMessage || "Failed to load reconciliation data.");
        setTableData([]);
        return;
      }
      const raw = data.inventoryReconciliationData ?? [];
      const rows = Array.isArray(raw) ? raw.map((row, idx) => mapReconciliationRowToTable(row, idx)) : [];
      setTableData(rows);
    };

    const url = `${getApiBase()}/fetchInventoryReconciliationData`;
    const payload = {
      brandOwnerMosymphonyID,
      vendorMosymphonyID,
      vendorPlant: ["all"],
      collabrixReconciliationID: selectedId,// 9899,
      materialTypeID: ["all"],
      sponsorMaterialCode: "all",
      filterRowsWith: "all",
    };

    axios
      .post(url, payload)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.status) {
          processTableResponse(res.data);
        } else {
          setTableError("Something went wrong. Please try again later.");
          setTableData([]);
        }

      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err?.response?.data?.errorMessage || err?.response?.data?.message || err?.message || "Failed to load table data.";
          setTableError(msg);
          setTableData([]);
        }
      })
      .finally(() => {
        if (!cancelled) setTableLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, selectedId, vendorMosymphonyID]);

  const handleClose = () => {
    if (typeof onClose === "function") onClose();
  };

  const handleSelectHistory = (item) => {
    const id = item.collabrixReconciliationID;
    if (id != null) setSelectedId(id);
  };

  const handleDownloadPdf = () => {
    if (tableData.length === 0) return;
    setPdfError(null);
    setPdfLoading(true);
    try {
      const headers = TABLE_COLUMNS.map((col) => escapeHtml(col.label));
      const headerRow = `<tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>`;
      const bodyRows = tableData
        .map(
          (row, idx) =>
            `<tr class="${idx % 2 === 1 ? "alt" : ""}">${TABLE_COLUMNS.map(
              (col) => `<td>${escapeHtml(getCellValue(row, col.id))}</td>`
            ).join("")}</tr>`
        )
        .join("");

      const printHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Reconciliation History - Table</title>
  <style>
    * { box-sizing: border-box; }
    @page { margin: 0; size: auto; }
    body { margin: 0; padding: 16px; font-family: Arial, sans-serif; font-size: 14px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    table { width: 100%; border-collapse: collapse; min-width: 650px; }
    th, td { padding: 8px 12px; text-align: left; border: 1px solid #ccc; font-size: 0.875rem; }
    th { background-color: #FFE4C2 !important; border-top: 1px solid #ffab91; border-bottom: 1px solid #ffab91; font-weight: 500; }
    tr.alt td { background-color: #f7f6f6 !important; }
    @media print {
      body { padding: 0; }
      @page { margin: 10mm; }
    }
  </style>
</head>
<body>
  <table>
    <thead>${headerRow}</thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`;

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.left = "-9999px";
      iframe.style.top = "0";
      iframe.style.width = "1200px";
      iframe.style.height = "800px";
      iframe.style.border = "none";
      document.body.appendChild(iframe);

      const blob = new Blob([printHtml], { type: "text/html;charset=utf-8" });
      const blobUrl = URL.createObjectURL(blob);

      const doPrint = () => {
        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
        } finally {
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
            if (iframe.parentNode) document.body.removeChild(iframe);
            setPdfLoading(false);
          }, 500);
        }
      };

      iframe.onload = () => {
        // Small delay so the document is fully rendered before print
        setTimeout(doPrint, 100);
      };
      iframe.src = blobUrl;
    } catch (err) {
      console.error(err);
      setPdfError("Download failed. Please try again.");
      setPdfLoading(false);
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      PaperProps={{ className: classes.dialogPaper }}
      aria-labelledby="view-reconciliation-history-title"
    >
      <DialogContent className={classes.dialogContent}>
        <Box
          style={{ margin: "1em", marginBottom: '0', display: "flex", gap: "1em" }}
        >
          <Typography
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={handleClose}
          >
            <ArrowBackIosIcon />
            Back
          </Typography>
          <Typography variant='h5' style={{ fontWeight: 600 }}>
            View Reconciliation History - {cmoName}
          </Typography>
        </Box>
        <Divider style={{ margin: "1em 0" }} />

        <div className={classes.layout}>
          <Paper className={`${classes.leftPanel} no-print`} elevation={0}>
            {/* <div className={classes.historySectionTitle}>History</div> */}
            {listError && (
              <div className={classes.errorBox}>
                {listError}
              </div>
            )}
            {listLoading ? (
              <div className={classes.loadingWrapper}>
                <CircularProgress size={32} />
              </div>
            ) : (
              <div className={classes.historyListWrapper}>
                <div className={classes.historyListInner}>
                  {historyList.length > 0 && (
                    <div
                      className={classes.historyTimelineLine}
                      aria-hidden="true"
                      style={{ top: 46, bottom: 0 }}
                    />
                  )}
                  <ul className={classes.historyList}>
                    {historyList.map((item, index) => {
                      const id = item.collabrixReconciliationID;
                      const isSelected = id === selectedId;
                      const lines = [cmoName, item.reconciliationDateTimeStamp].filter(Boolean)
                      return (
                        <li key={id ?? index} style={{ listStyle: "none" }}>
                          <div
                            className={`${classes.historyItem} ${isSelected ? classes.historyItemSelected : ""}`}
                            onClick={() => handleSelectHistory(item)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && e.preventDefault() && handleSelectHistory(item)}
                          >
                            <div style={{ border: '2px solid #fff', borderRadius: '50%', marginRight: 14 }}>
                              <div className={`${classes.historyNumber} `}>
                                {/* ${!isSelected ? classes.historyNumberInactive : ""} */}
                                {index + 1}
                              </div>
                            </div>
                            <div className={`${classes.historyLabel} ${isSelected ? classes.historyLabelText : classes.historyLabelLink}`}>
                              {lines.map((line, i) => (
                                <Typography
                                  key={i}
                                  className={`${classes.historyLabelLine} ${isSelected && i === 0 ? classes.historyLabelLineFirst : ""}`}
                                >
                                  {line}
                                </Typography>
                              ))}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                {historyList.length === 0 && !listError && (
                  <Typography className={classes.emptyState}>No history found.</Typography>
                )}
              </div>
            )}
          </Paper>

          <div className={classes.rightPanel}>
            <div className={`${classes.filePreviewHeader} no-print`}>
              <Typography className={classes.filePreviewTitle}>File Preview</Typography>
              <Box>
                <Button
                  variant="contained"
                  className={classes.downloadPdfButton}
                  onClick={handleDownloadPdf}
                  disabled={tableLoading || tableData.length === 0 || pdfLoading}
                >
                  {pdfLoading ? "Preparing…" : "Download PDF"}
                </Button>
                {/* <Typography variant="caption" display="block" className={classes.pdfHint}>
                In the print dialog, uncheck &quot;Headers and footers&quot; to remove URL and date from the PDF (table and page number only).
              </Typography> */}
              </Box>
            </div>
            {pdfError && (
              <div className={`${classes.errorBox} no-print`}>
                {pdfError}
              </div>
            )}
            {tableError && (
              <div className={`${classes.errorBox} no-print`}>{tableError}</div>
            )}
            <TableContainer className={classes.tableContainer}>
              {tableLoading ? (
                <div className={`${classes.loadingWrapper} no-print`}>
                  <CircularProgress size={32} />
                </div>
              ) : (
                <Table className={classes.table} size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {TABLE_COLUMNS.map((col) => (
                        <TableCell key={col.id}>{col.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.map((row, idx) => (
                      <TableRow key={row.id ?? idx} className={idx % 2 === 1 ? classes.grayRow : ""}>
                        {TABLE_COLUMNS.map((col) => {
                          const val = row[col.id] ?? row[col.id.replace(/([A-Z])/g, "_$1").toLowerCase()] ?? "";
                          return <TableCell key={col.id}>{String(val)}</TableCell>;
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {!tableLoading && tableData.length === 0 && !tableError && (
                <Typography className={`${classes.emptyState} no-print`}>Select a history entry to view data.</Typography>
              )}
            </TableContainer>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
